import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  MapPin, 
  Users, 
  ExternalLink,
  Calendar,
  MessageCircle,
  Globe
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EventsTable } from "@/components/EventsTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Meetup and Facebook icons as SVG components
const MeetupIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current text-[#ED1C40]">
    <path d="M6.98.555a.518.518 0 0 0-.105.011.53.53 0 1 0 .222 1.04.533.533 0 0 0 .409-.63.531.531 0 0 0-.526-.421zm6.455.638a.984.984 0 0 0-.514.143.99.99 0 1 0 1.02 1.699.99.99 0 0 0 .34-1.36.992.992 0 0 0-.846-.482zm6.992 3.039a.726.726 0 0 0-.396.122.727.727 0 1 0 .808 1.209.727.727 0 0 0-.412-1.33zm-16.207.6a.932.932 0 0 0-.921.8c-.07.506.287.972.795 1.04a.933.933 0 0 0 1.04-.793.932.932 0 0 0-.795-1.04.923.923 0 0 0-.119-.007zm9.426.602c-.063 0-.124.003-.186.008-1.21.09-2.243.717-2.908 1.591-.614.807-.966 1.818-.94 2.914.03 1.237.478 2.39 1.24 3.35.458.576 1.014 1.12 1.588 1.478.222.139.452.26.688.36 1.039-.12 1.789-.75 2.478-1.489.27-.29.533-.598.787-.918.37-.464.71-.957.97-1.49.24-.49.398-1.022.37-1.594-.03-.667-.234-1.252-.572-1.756-.51-.76-1.311-1.27-2.201-1.49-.32-.084-.65-.128-.984-.139-.053-.003-.107-.005-.16-.005-.056-.007-.113-.01-.17-.01zm-7.723 2.076a.714.714 0 0 0-.7.61.714.714 0 1 0 1.41.205.714.714 0 0 0-.607-.809.703.703 0 0 0-.103-.006zm14.063.73c.478 0 .947.1 1.39.293.443.192.844.472 1.168.823.648.705.96 1.667.837 2.614-.093.72-.407 1.396-.86 1.974a9.09 9.09 0 0 1-.968 1.043c-.367.338-.756.656-1.146.966-.768.61-1.554 1.2-2.241 1.865-.343.332-.664.684-.931 1.07-.268.386-.48.807-.59 1.262-.139.577-.063 1.18.204 1.698.265.515.703.922 1.221 1.158.517.238 1.095.305 1.65.198a2.721 2.721 0 0 0 1.482-.843c.375-.428.604-.978.611-1.546.007-.284-.048-.57-.156-.833a2.094 2.094 0 0 0-.484-.702.771.771 0 0 1 1.082-1.1c.33.325.586.72.756 1.153.172.434.254.9.243 1.364-.02.927-.394 1.824-1.015 2.532-.62.707-1.472 1.206-2.4 1.405-.927.2-1.9.1-2.765-.295-.863-.395-1.595-1.07-2.065-1.915-.468-.844-.654-1.828-.498-2.765.126-.752.447-1.456.876-2.085a8.83 8.83 0 0 1 1.136-1.3c.427-.401.88-.776 1.338-1.14.453-.36.911-.712 1.352-1.083.35-.295.688-.605.99-.946.303-.341.569-.716.76-1.127.19-.41.3-.862.3-1.319 0-.458-.115-.921-.338-1.33a2.48 2.48 0 0 0-.92-.947 2.44 2.44 0 0 0-1.3-.357c-.458 0-.912.12-1.3.359a2.514 2.514 0 0 0-.922.947.77.77 0 1 1-1.354-.734 4.017 4.017 0 0 1 1.49-1.537 4.003 4.003 0 0 1 2.086-.579zm-8.418.018a.634.634 0 0 0-.124.015.637.637 0 1 0 .249 1.25.64.64 0 0 0-.125-1.265zm-7.29 1.04c-.348.004-.684.135-.953.378a1.394 1.394 0 0 0-.455 1.203c.08.677.656 1.19 1.34 1.19.08 0 .158-.008.237-.024a1.393 1.393 0 0 0 1.166-1.578c-.074-.633-.553-1.124-1.172-1.163a1.442 1.442 0 0 0-.163-.006zm4.65 2.01a.77.77 0 0 0-.76.652.77.77 0 1 0 1.519-.24.769.769 0 0 0-.759-.413zm-6.062 3.537a.517.517 0 0 0-.104.01.53.53 0 1 0 .218 1.04.531.531 0 0 0-.114-1.05zm2.434 2.242a.61.61 0 0 0-.12.013.625.625 0 0 0-.49.734.625.625 0 0 0 .733.49.625.625 0 0 0-.123-1.237z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current text-[#1877F2]">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

export default function CityPage() {
  const { cityId } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCity = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/cities/${cityId}`);
        setCity(response.data);
        // Update page title for SEO
        document.title = response.data.seo_title;
      } catch (err) {
        console.error("Error fetching city:", err);
        setError("Ciudad no encontrada");
      } finally {
        setLoading(false);
      }
    };

    fetchCity();
    window.scrollTo(0, 0);
  }, [cityId]);

  if (loading) {
    return (
      <div className="pt-20" data-testid="city-page-loading">
        <div className="relative h-[50vh]">
          <Skeleton className="w-full h-full" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Skeleton className="h-10 w-2/3 mb-4" />
          <Skeleton className="h-24 w-full mb-8" />
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="city-page-error">
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-foreground mb-4">
            {error}
          </h1>
          <Link 
            to="/"
            className="btn-primary inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="city-page">
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px]" data-testid="city-hero">
        <img
          src={city.image}
          alt={`Intercambio de idiomas en ${city.name}`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        
        {/* Back Button */}
        <Link 
          to="/"
          className="absolute top-24 left-4 sm:left-8 glass px-4 py-2 rounded-full flex items-center gap-2 text-foreground hover:bg-white transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Volver</span>
        </Link>

        {/* City Title */}
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-12">
          <div className="container mx-auto">
            <div className="flex items-center gap-4 mb-3">
              <span className="text-4xl">{city.flag}</span>
              <Badge variant="secondary" className="bg-secondary text-secondary-foreground">
                <MapPin className="w-3 h-3 mr-1" />
                {city.country}
              </Badge>
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">
              Intercambio de idiomas en {city.name}
            </h1>
          </div>
        </div>
      </section>

      {/* SEO Description */}
      <section className="py-12 bg-white border-b border-border" data-testid="city-description">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-muted-foreground leading-relaxed max-w-4xl">
            {city.seo_description}
          </p>
        </div>
      </section>

      {/* Meetup & Facebook Groups */}
      <section className="py-12 md:py-16" data-testid="groups-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Meetup Groups */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden" data-testid="meetup-groups">
              <CardHeader className="bg-[#ED1C40]/5 border-b border-[#ED1C40]/10">
                <CardTitle className="flex items-center gap-3">
                  <MeetupIcon />
                  <span className="font-heading">Grupos de Meetup</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y divide-border">
                  {city.meetup_groups.map((group, index) => (
                    <li key={index} className="p-4 hover:bg-muted/50 transition-colors">
                      <a 
                        href={group.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between group"
                        data-testid={`meetup-group-${index}`}
                      >
                        <div>
                          <p className="font-medium text-foreground group-hover:text-[#ED1C40] transition-colors">
                            {group.name}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <Users className="w-3.5 h-3.5" />
                            {group.members.toLocaleString()} miembros
                          </p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-[#ED1C40] transition-colors" />
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Facebook Groups */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden" data-testid="facebook-groups">
              <CardHeader className="bg-[#1877F2]/5 border-b border-[#1877F2]/10">
                <CardTitle className="flex items-center gap-3">
                  <FacebookIcon />
                  <span className="font-heading">Grupos de Facebook</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y divide-border">
                  {city.facebook_groups.map((group, index) => (
                    <li key={index} className="p-4 hover:bg-muted/50 transition-colors">
                      <a 
                        href={group.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between group"
                        data-testid={`facebook-group-${index}`}
                      >
                        <div>
                          <p className="font-medium text-foreground group-hover:text-[#1877F2] transition-colors">
                            {group.name}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <Users className="w-3.5 h-3.5" />
                            {group.members.toLocaleString()} miembros
                          </p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-[#1877F2] transition-colors" />
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Lingua2 Featured Section */}
          <div className="mt-8" data-testid="lingua2-section">
            <Card className="border-2 border-primary/20 shadow-lg rounded-2xl overflow-hidden bg-gradient-to-r from-primary/5 to-secondary/5">
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center flex-shrink-0">
                    <Globe className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="font-heading font-bold text-xl text-foreground mb-2">
                      Lingua2 Language Exchange
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Encuentra más eventos de intercambio de idiomas en {city.name} y otras ciudades del mundo en Lingua2, 
                      la plataforma líder para conectar con hablantes nativos y practicar idiomas en persona.
                    </p>
                    <a 
                      href="https://www.languageexchanges.com"
                      className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
                      data-testid="lingua2-link"
                    >
                      Visitar Lingua2
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      <section className="py-12 md:py-16 bg-muted/30" data-testid="events-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-header mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-6 h-6 text-primary" />
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                Eventos de la semana
              </h2>
            </div>
            <p className="text-muted-foreground">
              Calendario de intercambios de idiomas en {city.name}
            </p>
          </div>

          <EventsTable events={city.weekly_events} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary" data-testid="cta-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageCircle className="w-12 h-12 text-white/80 mx-auto mb-4" />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-4">
            ¿Listo para practicar?
          </h2>
          <p className="text-white/80 mb-6 max-w-lg mx-auto">
            Únete a uno de los grupos de arriba y comienza a conocer gente 
            que comparte tu pasión por los idiomas.
          </p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-full font-medium hover:bg-white/90 transition-colors"
          >
            Explorar más ciudades
          </Link>
        </div>
      </section>
    </div>
  );
}
